<div class="admin-ribbon">
  <mat-icon>admin_panel_settings</mat-icon>
  <span i18n>Technical Administration Console</span>
</div>

<div class="login-container">
  <div class="login-wrap">
    <div class="logo-div">
      <img
        src="assets/logo/ServiceWebLogo.png"
        alt="Adaptive Service Logo"
      />
      <div class="admin-badge">
        <mat-icon>shield</mat-icon>
        <span i18n>ADMIN</span>
      </div>
    </div>

    <div class="form-div">
      <div class="mobile-logo-div">
        <img
          src="assets/logo/ServiceWebLogoMobile.png"
          alt="Adaptive Service Logo"
        />
        <div class="admin-badge">
          <mat-icon>shield</mat-icon>
          <span i18n>ADMIN</span>
        </div>
      </div>

      <mat-card class="login-card">
        @switch (authService.authStep()) {
          @case ("login") {
            <form [formGroup]="loginForm">
              <mat-card-title i18n>Service Admin Console</mat-card-title>
              <mat-card-subtitle i18n
                >Please log in to the system</mat-card-subtitle
              >
              <mat-card-content>
                <div class="flex-col">
                  <mat-form-field appearance="outline" subscriptSizing="dynamic">
                    <input matInput placeholder="email" formControlName="email" data-allow-autofill />
                  </mat-form-field>
                  <mat-form-field appearance="outline" subscriptSizing="dynamic">
                    <input
                      type="password"
                      matInput
                      placeholder="password"
                      formControlName="password"
                      autocomplete="current-password"
                      data-allow-autofill
                      i18n-placeholder
                    />
                  </mat-form-field>
                </div>
              </mat-card-content>
              <mat-card-actions>
                @if (!loading()) {
                  <button
                    matButton="elevated"
                    type="submit"
                    [disabled]="loginForm.invalid"
                    color="primary"
                    (click)="login()"
                  >
                    Login
                  </button>
                } @else {
                  <mat-spinner [diameter]="55" color="accent" />
                }
              </mat-card-actions>
            </form>
          }

          @case ("mfa_selection") {
            <form [formGroup]="selectionForm">
              <mat-card-title i18n>Service Admin Console</mat-card-title>
              <mat-card-subtitle i18n
                >Select the number where we'll send the code</mat-card-subtitle
              >
              <mat-card-content>
                <div class="flex-col">
                  <mat-selection-list
                    class="hint-list"
                    [multiple]="false"
                    formControlName="selectedHintIndex"
                  >
                    @for (
                      hint of authService.phoneHints();
                      track hint.uid;
                      let idx = $index
                    ) {
                      <mat-list-option [value]="idx">
                        {{ getHintLabel(hint) }}
                      </mat-list-option>
                    }
                  </mat-selection-list>
                </div>
              </mat-card-content>
              <mat-card-actions class="mfa-actions">
                @if (!loading()) {
                  <button
                    matButton
                    type="button"
                    (click)="cancelMfa()"
                    i18n="@@backButton"
                  >
                    Back
                  </button>
                  <button
                    matButton="elevated"
                    type="submit"
                    [disabled]="selectionForm.invalid"
                    color="primary"
                    (click)="sendCodeToSelectedNumber()"
                    i18n
                  >
                    Send the code
                  </button>
                } @else {
                  <mat-spinner [diameter]="55" color="accent" />
                }
              </mat-card-actions>
            </form>
          }

          @case ("mfa") {
            <form [formGroup]="mfaForm">
              <mat-card-title i18n>Service Admin Console</mat-card-title>
              @if (authService.selectedHint()?.factorId === "phone") {
                <mat-card-subtitle i18n
                  >We sent an SMS code to your phone number</mat-card-subtitle
                >
              } @else {
                <mat-card-subtitle i18n
                  >Enter the verification code from the Authenticator
                  App</mat-card-subtitle
                >
              }
              <mat-card-content>
                <div class="flex-col no-gap">
                  <mat-form-field appearance="outline" subscriptSizing="dynamic">
                    <mat-label i18n="@@6digitCodeInfo">6-digit code</mat-label>
                    <input
                      matInput
                      type="text"
                      formControlName="code"
                      maxlength="6"
                    />
                  </mat-form-field>

                  @if (authService.selectedHint()?.factorId === "phone") {
                    <div class="resend-section">
                      @if (resendCooldown() > 0) {
                        <p class="mat-body-2 text-muted" i18n>
                          Resend code in
                          <strong>{{ resendCooldown() }} s.</strong>
                        </p>
                      } @else {
                        <button
                          matButton
                          type="button"
                          color="primary"
                          (click)="resendCode()"
                          [disabled]="loading()"
                          i18n
                        >
                          Resend code
                        </button>
                      }
                    </div>
                  } @else if (authService.hasSmsAlternatives()) {
                    <div class="switch-sms-section">
                      <button
                        matButton
                        type="button"
                        color="accent"
                        (click)="switchToSmsFallback()"
                        [disabled]="loading()"
                        i18n
                      >
                        Change authentication method to SMS
                      </button>
                    </div>
                  }
                </div>
              </mat-card-content>
              <mat-card-actions class="mfa-actions">
                @if (!loading()) {
                  <button
                    matButton
                    type="button"
                    (click)="cancelMfa()"
                    i18n="@@backButton"
                  >
                    Back
                  </button>
                  <button
                    matButton="elevated"
                    type="submit"
                    [disabled]="mfaForm.invalid"
                    color="primary"
                    (click)="verifyMfa()"
                    i18n="@@verifyButton"
                  >
                    Verify
                  </button>
                } @else {
                  <mat-spinner [diameter]="55" color="accent" />
                }
              </mat-card-actions>
            </form>
          }

          @case ("totp_setup") {
            <form [formGroup]="mfaForm">
              <mat-card-title i18n>Service Admin Console</mat-card-title>
              <mat-card-subtitle i18n
                >Secure your account by enabling two-step verification using an
                app such as Google Authenticator, Authy, or Microsoft
                Authenticator.</mat-card-subtitle
              >
              <mat-card-content>
                <div class="qr-step">
                  <p class="mat-body-1" i18n>
                    <strong>1.</strong> Scan the QR code below in your app:
                  </p>
                  <div class="qr-container">
                    @if (authService.qrCodeUrl()) {
                      <qrcode
                        [qrdata]="authService.qrCodeUrl()!"
                        [width]="200"
                        [errorCorrectionLevel]="'M'"
                      />
                    }
                  </div>
                  <p class="mat-body-1" i18n>
                    <strong>2.</strong> Enter the 6-digit code from the app to
                    confirm:
                  </p>
                  <div class="flex-col no-gap">
                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                      <mat-label i18n="@@6digitCodeInfo"
                        >6-digit code</mat-label
                      >
                      <input
                        matInput
                        type="text"
                        formControlName="code"
                        maxlength="6"
                        autocomplete="off"
                      />
                    </mat-form-field>
                  </div>
                </div>
              </mat-card-content>
              <mat-card-actions class="mfa-actions totp-actions">
                @if (!loading()) {
                  <div class="totp-actions-row">
                    <button
                      matButton
                      type="button"
                      (click)="cancelTotpSetup()"
                      i18n="@@cancel"
                    >
                      Cancel
                    </button>
                    <button
                      matButton="elevated"
                      type="submit"
                      color="primary"
                      [disabled]="mfaForm.invalid"
                      (click)="enrollTotpSetup()"
                      i18n="@@verifi&saveButton"
                    >
                      Verify and save
                    </button>
                  </div>
                  <div class="totp-skip-row">
                    <button
                      matButton
                      color="warn"
                      type="button"
                      class="skip-btn"
                      (click)="skipTotpSetup()"
                      i18n
                    >
                      Skip setup (not recommended)
                    </button>
                  </div>
                } @else {
                  <mat-spinner [diameter]="55" color="accent" />
                }
              </mat-card-actions>
            </form>
          }
        }
      </mat-card>
    </div>
  </div>
</div>
<div id="recaptcha-container"></div>
